import axios from "axios";
import store from "@/redux/store";
import { setUser } from "@/slices/userSlice";
import baseGoApi from "./baseGoApi";
import { getCookie } from "@/utils/cookieUtils";

// Get the base URL from the environment variable (single source of truth for player-hub)
const baseURL = import.meta.env.VITE_PLAYER_HUB_URL || "https://player-hub-stage.balancegolf.com";

export const playerHubApi = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

playerHubApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Add cache control headers to prevent caching issues on mobile browsers
    config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    config.headers["Pragma"] = "no-cache";
    config.headers["Expires"] = "0";
    return config;
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const RENEW_TIMEOUT_MS = 60000; // 60s - Cloud Run cold start can take 30-60s
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

const refreshToken = async (): Promise<string> => {
    const currentPath = window.location.pathname;
    const publicRoutes = ['/login', '/privacy-policy'];
    const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

    if (isPublicRoute) {
        throw new Error("No valid session on public route");
    }

    const goApiBase = import.meta.env.VITE_GO_API_BASE_URL || import.meta.env.VITE_GO_API_URL;

    const doRenew = async (): Promise<string> => {
        const response = await axios.post(
            goApiBase + `/go-api/auth/renew_jwt`,
            {},
            {
                params: { ts: Date.now() },
                withCredentials: true,
                timeout: RENEW_TIMEOUT_MS,
                headers: {
                    "X-CSRF-Token": getCookie("csrf_token") ?? "",
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        const newToken = response.data?.jwt_token;
        const msg = response.data?.message ?? "";
        if (!newToken) {
            const err: any = new Error(msg || "No jwt_token in response");
            err.response = response;
            throw err;
        }
        return newToken;
    };

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            const newToken = await doRenew();
            localStorage.setItem("jwt_token", newToken);
            return newToken;
        } catch (error: any) {
            const status = error?.response?.status;
            const msg = error?.response?.data?.message ?? "";
            const isNetworkOrTimeout =
                !error?.response &&
                (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || error?.message?.includes('timeout'));

            if (status === 401 || status === 400 || status === 403 || msg.toLowerCase().includes("csrf")) {
                console.error("Token refresh failed (invalid session). Logging out user.", error);
                localStorage.removeItem("jwt_token");
                store.dispatch(setUser(null));
                throw error;
            }

            if (isNetworkOrTimeout && attempt < MAX_RETRIES) {
                console.warn(`Token refresh attempt ${attempt}/${MAX_RETRIES} failed (backend may be waking up), retrying...`, error?.message || error);
                await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                continue;
            }

            console.error("Token refresh failed. Logging out user.", error);
            localStorage.removeItem("jwt_token");
            store.dispatch(setUser(null));
            throw error;
        }
    }

    throw new Error("Token refresh failed after retries");
};

const onRefreshed = (token: string) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

playerHubApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If backend reports deleted/inactive user, immediately log out and redirect to login with message
        if (error.response?.status === 401) {
            const backendMessage: string | undefined = error.response?.data?.message;
            if (backendMessage && backendMessage.includes("Unauthorized: User not found")) {
                try {
                    // Clear any auth state and tokens
                    localStorage.removeItem("jwt_token");
                    store.dispatch(setUser(null));
                } finally {
                    // Stash a one-time notice for the login screen
                    try {
                        sessionStorage.setItem(
                            "loginNotice",
                            JSON.stringify({ type: "inactive" })
                        );
                    } catch (_) {}
                    // Redirect to login
                    window.location.replace("/login");
                }
                // Prevent further processing
                return Promise.reject(error);
            }
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshSubscribers.push((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(axios(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshToken();
                isRefreshing = false;
                onRefreshed(newToken);

                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                throw refreshError;
            }
        }

        return Promise.reject(error);
    }
);

export interface ChatMessage {
    id: string;
    chatId: string;
    userId: string;
    /** Backend may return senderID instead of userId */
    senderID?: string;
    message: string;
    createdAt: string;
    updatedAt?: string;
    attachments?: ChatAttachment[];
    /** Backend may return attachmentURL as a single string */
    attachmentURL?: string;
}

export interface ChatAttachment {
    id: string;
    url: string;
    type: string;
    filename: string;
}

export interface ChatMember {
    id: string;
    chatId: string;
    userId: number;
    userName?: string;
    userEmail?: string;
    joinedAt: string;
}

export interface ChatMemberInfo {
    id: string;
    email: string;
    name: string;
    hcp?: number;
    profileImageURL?: string;
    createdAt: string;
    bgFacilityID?: number;
}

export interface Chat {
    id: string;
    createdAt: string;
    updatedAt?: string;
    members: ChatMemberInfo[];
    lastMessage?: ChatMessage;
    infoText?: string;
    title?: string;
    unreadCount?: number;
}

export interface ChatRecipient {
    id: string;
    name: string;
    email?: string;
    avatarPath?: string;
}

export interface CreateChatRequest {
    members: string[];
    title?: string;
}

export interface AddChatMemberRequest {
    userId: string;
}

export interface SendMessageRequest {
    message: string;
    /** A message carries at most one attachment — the API stores a single URL. */
    attachment?: File;
}

export interface PlayerSearchRequest {
    email: string;
}

export interface PlayerSearchResponse {
    user_id: string;
    /**
     * Indicates that the player profile was just created in player-hub
     * and an app download email was sent.
     */
    wasCreated?: boolean;
}

/**
 * Search for a player by email
 */
export const searchPlayerByEmail = async (email: string): Promise<PlayerSearchResponse> => {
    const response = await playerHubApi.post('/profile/search', { email });
    // For existing users we explicitly mark wasCreated as false for clarity
    return {
        ...response.data,
        wasCreated: false,
    };
};

/**
 * Search for a player by email, automatically creating the player if not found.
 * Hanterar både:
 * - HTTP-fel (500/404) från /profile/search
 * - Logiskt "not found" där svaret är 200 men body innehåller status 404 / "Player not found".
 */
export const searchPlayerByEmailOrCreate = async (email: string, name?: string): Promise<PlayerSearchResponse> => {
    // Gemensam hanterare för "not found"-fall
    const handlePlayerNotFound = async (): Promise<PlayerSearchResponse> => {
        try {
            console.log('User not found, creating player profile in player-hub...');
            // Create the player profile in player-hub
            const createResult = await createPlayerProfile(email, name);
            
            // Get the user_id (player-hub / coach-hub user id) from the created profile
            const playerHubUserId = 
                (createResult as any).user_id ??
                (createResult as any).userId ??
                (createResult as any).id;
            
            if (!playerHubUserId) {
                console.warn(
                    'createPlayerProfile did not return a user_id / userId / id field',
                    createResult
                );
                throw new Error(`Player profile created for ${email} but no user_id was returned`);
            }

            // Return the player-hub / coach-hub user_id and mark that the profile was just created
            // OBS: Själva app-download-mailet skickas i de flöden där vi vet rätt playerId (t.ex. PlayerInfo),
            // inte här, eftersom GO-API /player/:id/email/app_download/send kräver ett numeriskt playerId.
            return { user_id: String(playerHubUserId), wasCreated: true };
        } catch (createError) {
            console.error('Error creating player profile:', createError);
            throw createError;
        }
    };

    try {
        const result: any = await searchPlayerByEmail(email);

        // Nytt fall: backend svarar 200 men body innehåller "Player not found"
        const logicalNotFound =
            result?.status === 404 ||
            result?.title === 'Player not found';

        if (logicalNotFound) {
            return await handlePlayerNotFound();
        }

        return result;
    } catch (error: any) {
        // Check if it's a "not found" style error from /profile/search (user not found)
        // Backend can currently respond with 500 or 404 for this case
        const status = error.response?.status;
        if ((status === 500 || status === 404) &&
            error.config?.url?.includes('/profile/search')) {
            return await handlePlayerNotFound();
        }
        // Re-throw if it's not a 500/404 error from /profile/search
        throw error;
    }
};

/**
 * Create a player profile via player-hub
 * Skickar med extra info som golfklubb (org-namn) och bg_facility_id från Redux-state.
 */
export const createPlayerProfile = async (email: string, _name?: string): Promise<{ user_id: string; playerId?: number; id?: number }> => {
    // Hämta extra kontext från Redux (organisation & facility) för att enrich:a profilen
    const state = store.getState() as any;
    const organisation = state.organisation?.organisation;
    const facilities = state.facility?.facilities || organisation?.facilities || [];

    const golfClubName: string | undefined = organisation?.name;
    const bgFacilityId: number | undefined = facilities.length > 0 ? facilities[0].id : undefined;

    const payload: any = {
        email,
        create_in_coach_hub: false,
    };

    // Skicka med namn om vi har det
    if (_name) {
        payload.name = _name;
    }

    // Skicka med golfklubb (org-namnet)
    if (golfClubName) {
        payload.golf_club = golfClubName;
    }

    // Skicka med bg_facility_id om vi hittar en facility
    if (bgFacilityId) {
        payload.bg_facility_id = bgFacilityId;
    }

    // HCP känner vi inte alltid till här – om du vill kan vi senare utöka
    // searchPlayerByEmailOrCreate-signaturen så vi kan skicka med hcp också.

    const response = await playerHubApi.post('/profile/create-bg', payload);
    return response.data;
};

/**
 * Send app download email to player
 * :id här är det numeriska playerId i GO-API (t.ex. 229 från /player/summery/229)
 */
export const sendAppDownloadEmail = async (playerId: number): Promise<void> => {
    await baseGoApi.post(`/player/${playerId}/email/app_download/send`);
};

/**
 * Fetch all chats for the current user
 */
export const fetchChats = async (): Promise<Chat[]> => {
    const response = await playerHubApi.get('/chat');
    return response.data || [];
};

/**
 * Fetch a single chat (including members) by id
 * Uses GET /chat/:id – source of truth for current members.
 */
export const fetchChatById = async (chatId: string): Promise<Chat> => {
    const response = await playerHubApi.get(`/chat/${chatId}`);
    return response.data;
};

/**
 * Create a new chat (direct or group)
 */
export const createChat = async (request: CreateChatRequest): Promise<Chat> => {
    const response = await playerHubApi.post('/chat', request);
    return response.data;
};

/**
 * Update an existing chat's title and/or members.
 * PUT /chat/:id — when `members` is provided player-hub replaces the full member
 * set (always keeping the caller), so pass the complete desired member list.
 */
export interface UpdateChatRequest {
    title?: string;
    members?: string[];
}

export const updateChat = async (chatId: string, request: UpdateChatRequest): Promise<Chat> => {
    const response = await playerHubApi.put(`/chat/${chatId}`, request);
    return response.data;
};

/**
 * Player-hub responds with HTTP 400 `{ title: "unknown member", detail: "members not found: <uuid>, ..." }`
 * when one or more member UUIDs in a create/update-chat request don't exist in the users table.
 * Returns the extracted UUIDs (so callers can map them back to attendee names) or null if the
 * error is something else.
 */
export interface UnknownMemberError {
    unknownMemberIds: string[];
    rawDetail: string;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export const parseUnknownMemberError = (error: any): UnknownMemberError | null => {
    if (error?.response?.status !== 400) return null;
    const body = error.response.data ?? {};
    const detail = String(body.detail ?? '');
    if (body.title !== 'unknown member' && !detail.startsWith('members not found')) {
        return null;
    }
    return { unknownMemberIds: detail.match(UUID_RE) ?? [], rawDetail: detail };
};

/**
 * Fetch available recipients (players/users) to start a chat with
 */
export const fetchChatRecipients = async (): Promise<ChatRecipient[]> => {
    const response = await playerHubApi.get('/chat/recipients');
    return response.data || [];
};

/**
 * Fetch messages for a specific chat
 */
export const fetchChatMessages = async (
    chatId: string,
    limit = 10,
    offset = 0,
): Promise<ChatMessage[]> => {
    const response = await playerHubApi.get(`/chat/${chatId}/messages`, {
        params: { limit, offset, orderBy: 'created_at:desc' }
    });
    return response.data || [];
};

/**
 * Add one or more members to an existing chat
 * POST /chat/:id/chatmembers
 */
export const addChatMembers = async (chatId: string, request: AddChatMemberRequest): Promise<void> => {
    await playerHubApi.post(`/chat/${chatId}/chatmembers`, request);
};

/**
 * Remove a single member from an existing chat
 * DELETE /chat/:id/chatmembers/:userId
 */
export const removeChatMember = async (chatId: string, userId: string): Promise<void> => {
    await playerHubApi.delete(`/chat/${chatId}/chatmembers/${encodeURIComponent(userId)}`);
};

/**
 * Upload a chat attachment to Player Hub (POST /files/upload).
 * Returns the unsigned storage path: the backend re-signs the URL on every read, so
 * the signing query params must not be stored on the message.
 */
const uploadChatAttachment = async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await playerHubApi.post<{ fileUrl?: string; signedUrl?: string }>('/files/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = res.data?.fileUrl;
    if (typeof url !== 'string') throw new Error('Upload response missing fileUrl');
    return url.split(/[?]|%3F/i)[0];
};

/**
 * Send a message to a chat.
 * POST /chat/:id/messages accepts JSON only ({ message, attachmentURL }), so an
 * attachment is uploaded first and the message carries its storage path. The response
 * — and the websocket echo — come back with that URL already signed, ready to render.
 */
export const sendMessage = async (chatId: string, request: SendMessageRequest): Promise<ChatMessage> => {
    const attachmentURL = request.attachment
        ? await uploadChatAttachment(request.attachment)
        : undefined;

    const response = await playerHubApi.post(`/chat/${chatId}/messages`, {
        message: request.message || '',
        attachmentURL,
    });
    return response.data;
};

/**
 * Get unread chat count for badge
 */
export const fetchUnreadChatCount = async (): Promise<number> => {
    try {
        const chats = await fetchChats();
        return chats.reduce((count, chat) => count + (chat.unreadCount || 0), 0);
    } catch (error) {
        console.error('Error fetching unread chat count:', error);
        return 0;
    }
};

/**
 * Mark a chat as read for the current user — moves last_read_at forward.
 */
export const markChatAsRead = async (chatId: string): Promise<void> => {
    await playerHubApi.post(`/chat/${chatId}/read`);
};

/**
 * Fetch all media files for a player (from trainings, etc.)
 */
export const fetchPlayerMedia = async (playerId: number): Promise<Array<{ id: number; url: string; type?: string; filename: string }>> => {
    try {
        // Fetch all trainings for the player
        const trainings = await baseGoApi.get(`/player/${playerId}/playertraining`);
        const allMedia: Array<{ id: number; url: string; type?: string; filename: string }> = [];
        
        // Collect all media from trainings
        if (Array.isArray(trainings.data)) {
            trainings.data.forEach((training: any) => {
                if (training.mediaAttachments && Array.isArray(training.mediaAttachments)) {
                    training.mediaAttachments.forEach((media: any) => {
                        allMedia.push({
                            id: media.id,
                            url: media.url,
                            type: media.type,
                            filename: media.filename || `media-${media.id}`,
                        });
                    });
                }
            });
        }
        
        return allMedia;
    } catch (error) {
        console.error('Error fetching player media:', error);
        return [];
    }
};
